import { useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';
import { signOut, useSession } from '@ghq-abi/auth-client-lib/client';
import { useTranslate } from '@tolgee/react';
import nprogress from 'nprogress';

import { useAbility } from '~/core/contexts/AbilityContext';
import { useSessionInfo } from '~/core/contexts/SessionInfoContext';
import { Icon } from '~/shared/components';
import { SignOut } from '~/shared/components/icons/SignOut';
import { Action } from '~/shared/types/Permission';
import { isProduction } from '~/shared/utils/environments';

type UserInfo = {
  globalId?: string;
  name?: string;
  zone?: string;
};

type UseUserDropdownProps = {
  user: UserInfo;
};

export const useUserDropdown = ({ user }: UseUserDropdownProps) => {
  const { t } = useTranslate(['default']);
  const { proxyOut } = useSession();
  const { sessionInfo } = useSessionInfo();
  const router = useRouter();
  const ability = useAbility();
  const isProd = isProduction();

  const isProxying = !!sessionInfo?.proxiedAs;

  function handleSignOut() {
    void signOut({ redirect: false });
  }

  const goToProxy = useCallback(() => {
    void router.push('/proxy');
  }, [router]);

  const handleStopProxy = useCallback(async () => {
    nprogress.start();
    await proxyOut();
    nprogress.done();
    await router.push('/home');
    // Force reload the page to update the session with logged user data
    router.reload();
  }, [proxyOut, router]);

  const items = useMemo(() => {
    const dropdownItems = [
      {
        label: t('common.sign_out'),
        onSelect: handleSignOut,
        icon: <SignOut />
      }
    ];

    if (!isProd && ability.can('proxy', 'User')) {
      dropdownItems.unshift({
        label: t(isProxying ? 'common.stop_proxy' : 'common.proxy_as_user'),
        onSelect: isProxying ? handleStopProxy : goToProxy,
        icon: isProxying ? (
          <Icon.StopProxy size={18} />
        ) : (
          <Icon.ProxyIcon size={18} />
        )
      });
    }

    if (!isProd && ability.can(Action.StopProxy, 'User')) {
      dropdownItems.unshift({
        label: t('common.stop_proxy'),
        onSelect: handleStopProxy,
        icon: <Icon.StopProxy size={18} />
      });
    }

    return dropdownItems;
  }, [ability, goToProxy, handleStopProxy, isProd, isProxying, t]);

  const profileInfo = {
    name: user.name ?? '',
    zone: user.zone ?? ''
  };

  return {
    items,
    profileInfo
  };
};
